import { Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ArrowRight,
  Building2,
  Network,
  Users,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useStructureData } from '@/pages/structure/hooks/useStructureQueries';
import { useTeam } from '@/pages/team/hooks/useTeamQueries';

/**
 * CompanyLanding — обзорный лендинг раздела «Компания» (/company, AK-350).
 *
 * Карточки-саммари (Хозяйств · Полей · Людей · Приглашений) из существующих
 * хуков structure/team — каждая ведёт в соответствующую вкладку. Ленту событий
 * не делаем (нет бэка). Пустое хозяйство — нули + CTA в подраздел; сбой
 * загрузки — прочерк и явная плашка (AK-572): раньше и то и другое рисовалось
 * нулём, и «пусто» было неотличимо от «сломалось».
 */

interface SummaryCard {
  key: string;
  /** Ключ вкладки назначения (сегмент /company/<tab>). */
  tab: string;
  icon: typeof Building2;
  value: string;
  label: string;
  cta: string;
  loading: boolean;
  /** Данные не приехали — показываем прочерк, а не выдуманный ноль. */
  unavailable: boolean;
}

function StatCard({ card }: { card: SummaryCard }) {
  const Icon = card.icon;
  return (
    <Link to="/company/$tab" params={{ tab: card.tab }} className="group">
      <Card className="flex h-full flex-col p-4 transition-colors hover:border-primary/40 hover:bg-accent/40">
        <span className="mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-secondary text-muted-foreground">
          <Icon className="h-4.5 w-4.5" />
        </span>
        {card.loading ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <span
            className={
              card.unavailable
                ? 'text-2xl font-bold leading-none text-muted-foreground'
                : 'text-2xl font-bold tabular-nums leading-none'
            }
          >
            {card.unavailable ? '—' : card.value}
          </span>
        )}
        <span className="mt-1 text-[13px] text-muted-foreground">
          {card.label}
        </span>
        <span className="mt-3 flex items-center gap-1 text-xs font-semibold text-primary">
          {card.cta}
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </Card>
    </Link>
  );
}

export function CompanyLanding() {
  const structure = useStructureData(false);
  const team = useTeam();

  const farmCount = structure.farms.filter((f) => !f.archived).length;
  const fieldCount = structure.farms.reduce(
    (sum, f) => sum + (f.field_count ?? 0),
    0,
  );
  const members = team.data?.members ?? [];
  const invitations = team.data?.pending_invitations ?? [];

  const structureLoading = structure.isLoading;
  const teamLoading = team.isLoading;
  // Сбой загрузки раньше рисовался нулём — «0 хозяйств» и «упало» выглядели
  // одинаково. Для свежего клиента это худший вариант: он не отличает пустую
  // систему от сломанной.
  const structureFailed = !!structure.error;
  const teamFailed = !!team.error;
  const somethingFailed = structureFailed || teamFailed;

  // Свежий тенант: компании ещё нет, значит и хозяйств завести нельзя.
  const noCompany =
    !structureLoading && !structureFailed && structure.companyId === null;

  const cards: SummaryCard[] = [
    {
      key: 'farms',
      tab: 'structure',
      icon: Network,
      value: String(farmCount),
      label:
        fieldCount > 0
          ? `Хозяйств · ${fieldCount} ${fieldCount === 1 ? 'поле' : 'полей'}`
          : 'Хозяйств',
      cta: 'Открыть структуру',
      loading: structureLoading,
      unavailable: structureFailed,
    },
    {
      key: 'fields',
      tab: 'structure',
      icon: Building2,
      value: String(fieldCount),
      label: 'Полей',
      // Соседние плитки ведут в одно и то же место — подпись одна (было
      // «К структуре» рядом с «Открыть структуру», design review 18.08 А11).
      cta: 'Открыть структуру',
      loading: structureLoading,
      unavailable: structureFailed,
    },
    {
      key: 'people',
      tab: 'team',
      icon: Users,
      value: String(members.length),
      label: 'Людей в команде',
      cta: 'Управлять командой',
      loading: teamLoading,
      unavailable: teamFailed,
    },
    {
      key: 'invites',
      tab: 'team',
      icon: Users,
      value: String(invitations.length),
      label: 'Приглашений в ожидании',
      cta: 'Открыть приглашения',
      loading: teamLoading,
      unavailable: teamFailed,
    },
  ];

  return (
    <div className="space-y-4">
      {noCompany && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">
              Компания ещё не заведена — с неё начинается всё остальное:
              хозяйства, поля, планирование сезона.
            </span>
            <Button asChild size="sm">
              <Link to="/company/$tab" params={{ tab: 'profile' }}>
                Заполнить реквизиты
              </Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {somethingFailed && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {structureFailed && teamFailed
              ? 'Ни структура, ни команда не загрузились — в карточках прочерк, а не ноль.'
              : structureFailed
                ? 'Структура не загрузилась — в её карточках прочерк, а не ноль.'
                : 'Команда не загрузилась — в её карточках прочерк, а не ноль.'}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((card) => (
          <StatCard key={card.key} card={card} />
        ))}
      </div>
    </div>
  );
}

export default CompanyLanding;
